import { changeCase } from '../case/case';
import { isString } from '../hidash/hidash';
import { P, match } from '../match';
import type { Asyncify } from '../types';

type APICustomError = { data: { code: number; detail: string }; status: number };

type APIValidationError = {
  data: {
    code: number;
    detail: string;
    errors: [{ field: string; message: string }];
  };
  status: number;
};

export const isAPICustomError = (error: unknown): error is APICustomError =>
  match(error)
    .with(
      {
        data: { code: P.number, detail: P.string },
        status: P.number,
      },
      () => true,
    )
    .otherwise(() => false);

export const isAPIValidationError = (error: unknown): error is APIValidationError =>
  match(error)
    .with(
      {
        data: {
          code: P.number,
          detail: P.string,
          errors: P.array({ field: P.string, message: P.string }),
        },
        status: P.number,
      },
      () => true,
    )
    .otherwise(() => false);

export const extractApiError = (error: unknown): MaybeUndefined<string> => {
  if (isAPIValidationError(error)) {
    return error.data.errors.reduce(
      (acc, { field, message }) => acc.concat(`${changeCase(field, 'sentenceCase')}: ${message}\n`),
      '',
    );
  } else if (isAPICustomError(error)) {
    return error.data.detail;
  } else if (isString(error)) {
    return String(error);
  }
  return undefined;
};

export const extractApiErrorField = (error: unknown): MaybeUndefined<string> => {
  if (isAPIValidationError(error)) {
    return changeCase(error.data.errors[0].field, 'camelCase');
  }
  return undefined;
};

export const FALLBACK_ERROR_MESSAGE = 'Something went wrong. Try again later.';
export const FALLBACK_SUCCESS_MESSAGE = 'Success!';

// Not sure where to best place this-- the config should generally be the same across all platforms
export type SentryBreadcrumb = {
  category?: string;
  data?: Record<string, unknown>;
  level?: 'debug' | 'error' | 'fatal' | 'info' | 'log' | 'warning';
  message?: string;
  timestamp?: number;
  type?: 'debug' | 'default' | 'error' | 'info';
};

export type HandleMutationOptions<Result> = {
  errorBreadcrumb?: SentryBreadcrumb;
  errorMessage?: string;
  fallbackErrorMessage?: string;
  mutation: Asyncify<() => Result>;
  onComplete?: MaybeAsyncify<() => void>;
  onError?: MaybeAsyncify<(error: unknown) => void>;
  onSuccess?: MaybeAsyncify<(result: Result) => void>;
  /** @default true */
  showErrorToast?: boolean;
  /** @default true */
  showSuccessToast?: boolean;
  successMessage?: string;
};

// At a bare minimum all clients should have toast function that takes a superset of these props
type ToastConfig = {
  title: string;
  variant: 'danger' | 'success';
};

type MutationHandlerConfig = {
  addSentryBreadcrumb: (breadcrumb: SentryBreadcrumb) => void;
  showToast: (config: ToastConfig) => string | void;
};

export const getMutationHandler =
  ({ addSentryBreadcrumb, showToast }: MutationHandlerConfig) =>
  async <Result>({
    errorBreadcrumb,
    errorMessage,
    fallbackErrorMessage = FALLBACK_ERROR_MESSAGE,
    mutation,
    onComplete,
    onError,
    onSuccess,
    showErrorToast = true,
    showSuccessToast = true,
    successMessage,
  }: HandleMutationOptions<Result>) => {
    try {
      const result = await mutation();
      // Sometimes for some reason, no error will be thrown even though api is returning an error
      if (result !== null && typeof result === 'object' && 'error' in result) {
        const extractedErrorMessage = extractApiError(result.error);
        await onError?.(result.error);
        if (showErrorToast) {
          showToast({
            title: errorMessage ?? extractedErrorMessage ?? fallbackErrorMessage,
            variant: 'danger',
          });
        }
        await onComplete?.();
        return;
      }
      await onSuccess?.(result);
      if (showSuccessToast) {
        showToast({
          title: successMessage ?? FALLBACK_SUCCESS_MESSAGE,
          variant: 'success',
        });
      }
      await onComplete?.();
      return result;
    } catch (error) {
      addSentryBreadcrumb({
        category: errorBreadcrumb?.category,
        data: { stringifiedError: JSON.stringify(error), ...errorBreadcrumb?.data },
        level: errorBreadcrumb?.level ?? 'error',
        message: errorBreadcrumb?.message ?? `Error occurred in handleMutation, function: ${mutation.name}`,
        type: errorBreadcrumb?.type ?? 'error',
      });
      await onError?.(error);
      const extractedErrorMessage = extractApiError(error);
      if (showErrorToast) {
        showToast({
          title: errorMessage ?? extractedErrorMessage ?? fallbackErrorMessage,
          variant: 'danger',
        });
      }
      await onComplete?.();
      return;
    }
  };

export type MutationHandler = ReturnType<typeof getMutationHandler>;
